import { createSelector } from 'reselect'
import { ProjectResponse, ProjectType } from '@tabeeb/contracts/microservices/swagger-ai-service'
import { RootState } from '@tabeeb/store'
import { Selector } from 'react-redux'
import { AnnotationSource } from '../enums'
import { defaultPagesStatisticsModel } from '../services'
import { AnnotationSourceOption, AnnotationSourceSubOption, PagesStatistics } from '../types'

export const getPagesStatistics = (state: RootState): PagesStatistics[] => state.modelTraining.pagesStatistics
export const getSelectedProject = (state: RootState): ProjectResponse | null => state.modelTraining.selectedProject

const getCountedEntitiesName = (annotationSource: AnnotationSource): string => {
  switch (annotationSource) {
    case AnnotationSource.ImageAndBoundingBoxAnnotations:
      return 'bounding box annotation(s)'
    case AnnotationSource.ImageAndPolygonAnnotations:
      return 'polygon annotation(s)'
    case AnnotationSource.ImageAndClassificationAnnotations:
      return 'classification annotation(s)'
    case AnnotationSource.ImageAndQuestionPairs:
      return 'form question(s)'
    case AnnotationSource.ImageAndNlpTagSets:
      return 'NLP tag set(s)'
    default:
      return ''
  }
}

const getEntityName = (annotationSource: AnnotationSource): string => {
  switch (annotationSource) {
    case AnnotationSource.ImageAndBoundingBoxAnnotations:
    case AnnotationSource.ImageAndPolygonAnnotations:
    case AnnotationSource.ImageAndClassificationAnnotations:
      return 'CV class'
    case AnnotationSource.ImageAndQuestionPairs:
    case AnnotationSource.ImageAndNlpTagSets:
      return 'Form'
    default:
      return ''
  }
}

const getAnnotationSourceOptionText = (
  annotationSource: AnnotationSource,
  pagesCount: number,
  itemsOnPagesCount: number
): string => `${pagesCount} page(s) with ${itemsOnPagesCount || ''} ${getCountedEntitiesName(annotationSource)}`

const getAnnotationSourceSubOptionText = (
  entityName: PagesStatistics['Name'],
  annotationSource: AnnotationSource,
  pagesCount: number,
  itemsOnPagesCount: number
): string =>
  `${getEntityName(annotationSource)} '${entityName}': ${getAnnotationSourceOptionText(
    annotationSource,
    pagesCount,
    itemsOnPagesCount
  )}`

const getAnnotationSourceOption = (
  annotationSource: AnnotationSource,
  pagesCount: number,
  itemsOnPagesCount: number,
  subOptions: AnnotationSourceSubOption[] = []
): AnnotationSourceOption => ({
  id: annotationSource,
  value: getAnnotationSourceOptionText(annotationSource, pagesCount, itemsOnPagesCount),
  disabled: pagesCount === 0,
  subOptions,
})

const getAnnotationSourceSubOption = (
  annotationSource: AnnotationSource,
  pagesCount: number,
  itemsOnPagesCount: number,
  entityId: PagesStatistics['Id'],
  entityName: PagesStatistics['Name']
): AnnotationSourceSubOption => ({
  id: entityId,
  value: getAnnotationSourceSubOptionText(entityName, annotationSource, pagesCount, itemsOnPagesCount),
  disabled: pagesCount === 0,
})

const createOption = (
  pagesStatistics: PagesStatistics[],
  annotationSource: AnnotationSource,
  getPagesCount: (ps: PagesStatistics) => number,
  getItemsCount: (ps: PagesStatistics) => number
): AnnotationSourceOption => {
  const totalPagesStatistics = pagesStatistics.find((ps) => ps.Id === null) || defaultPagesStatisticsModel

  return getAnnotationSourceOption(
    annotationSource,
    getPagesCount(totalPagesStatistics),
    getItemsCount(totalPagesStatistics),
    getPagesCount(totalPagesStatistics) > 0
      ? pagesStatistics
          .filter((ps) => ps.Id !== null && getPagesCount(ps) > 0)
          .map((pagesStatisticsPerEntity) =>
            getAnnotationSourceSubOption(
              annotationSource,
              getPagesCount(pagesStatisticsPerEntity),
              getItemsCount(pagesStatisticsPerEntity),
              pagesStatisticsPerEntity.Id,
              pagesStatisticsPerEntity.Name
            )
          )
      : []
  )
}

export const getAnnotationSourceOptions: Selector<RootState, AnnotationSourceOption[]> = createSelector(
  [getPagesStatistics, getSelectedProject],
  (pagesStatistics, selectedProject) => {
    const result: AnnotationSourceOption[] = []

    if (!selectedProject) {
      return result
    }

    if (selectedProject.Type === ProjectType.ObjectDetection) {
      result.push(
        createOption(
          pagesStatistics,
          AnnotationSource.ImageAndBoundingBoxAnnotations,
          (ps) => ps.PagesCountAnnotationBbox,
          (ps) => ps.ItemsCountAnnotationBbox
        )
      )

      result.push(
        createOption(
          pagesStatistics,
          AnnotationSource.ImageAndPolygonAnnotations,
          (ps) => ps.PagesCountAnnotationPolygon,
          (ps) => ps.ItemsCountAnnotationPolygon
        )
      )
    } else if (selectedProject.Type === ProjectType.ImageClassification) {
      result.push(
        createOption(
          pagesStatistics,
          AnnotationSource.ImageAndClassificationAnnotations,
          (ps) => ps.PagesCountAnnotationAll,
          (ps) => ps.ItemsCountAnnotationAll
        )
      )
    } else if (selectedProject.Type === ProjectType.VQA) {
      result.push(
        createOption(
          pagesStatistics,
          AnnotationSource.ImageAndQuestionPairs,
          (ps) => ps.PagesCountFormQuestions,
          (ps) => ps.ItemsCountFormQuestions
        )
      )

      result.push(
        createOption(
          pagesStatistics,
          AnnotationSource.ImageAndNlpTagSets,
          (ps) => ps.PagesCountNlpTags,
          (ps) => ps.ItemsCountNlpTags
        )
      )
    }
    return result
  }
)

export const getEntitiesFromPagesStatistics: Selector<
  RootState,
  Record<number, { Id: number; Name: string }>
> = createSelector([getPagesStatistics], (pagesStatistics) => {
  return Object.fromEntries(
    pagesStatistics.filter((ps) => ps.Id !== null).map((ps) => [ps.Id, { Id: ps.Id, Name: ps.Name }])
  )
})
